<template>
  <div class="container">
    <div>
<!--      <div style="background-color: #5daf34;width: 100px;">www</div>-->
      <div>
        <h1>Using a trained PGMG model to generate molecules</h1>
        <div class="content1" id="first">
          <h2>Prepare the pharmacophore hypotheses</h2>
          <p>First of all, you need some pharmacophore hypotheses. A pharmacophore is defined as a set of
            chemical features and their spatial information that is necessary for a drug to bind to a target
            and there are many ways to acquire one.
          </p>
          <p>
            If you have a biochemistry background, we strongly encourage you to build it yourself by
            stacking active ligands or analyzing the receptor structure. There are also many tools available.
            And you can always adjust the input hypothesis according to the results.
          </p>
          <p>
            Apart from building it yourself, you can also acquire them by searching the literature or
            just randomly sampling 3-6 pharmacophore elements from a reference ligand to build some hypotheses
            and filtering the generated molecules afterwards.
          </p>
        </div>
        <br>
        <div class="content1">
          <h2>Format the hypotheses</h2>
          <p>
            The pharmacophore hypotheses need to be converted to a fully-connected graph and should be
            provided in one of the two formats:
          </p>
          <ul>
            <li>
              <p>the <strong>.posp</strong> format where the type of the pharmacophore points and the 3d positions are provided,
                see <el-link type="primary" style="font-size: 18px" href="https://www.csuligroup.com/PGMG/example.posp">
                  example.posp
                </el-link>
                for example.</p>
            </li>
            <li>
              <p>
                the <strong>.edgep</strong> format where the type of the pharmacophore points and the shortest-path-based
                distances between each point are provided, see <el-link type="primary" style="font-size: 18px" href="https://www.csuligroup.com/PGMG/example.edgep">example.edgep</el-link> for example.
              </p>
            </li>
          </ul>
          <p>
            Pharmacophore types supported by default:
          </p>
          <ul>
            <li><p>
              AROM: aromatic ring
            </p></li>
            <li><p>
              POSC: cation
            </p></li>
            <li><p>
              HACC: hydrogen bond acceptor
            </p></li>
            <li><p>
              HDON: hydrogen bond donor
            </p></li>
            <li><p>
              HYBL: hydrophobic group (ring)
            </p></li>
            <li><p>
              LHYBL: hydrophobic group (non-ring)
            </p></li>
          </ul>
          <p>
            The 3d position in <strong>.posp</strong> files will first be used to calculate the Euclidean distances between each
            point and then the distances will be mapped to the shortest-path-based distances.
          </p>
          <p>
            See the Supplemental Information of our paper for detailed descriptions.
          </p>
          <p>
            How to calculate shortest-path distances: The shortest path between pharmacophore point
            <strong>a</strong> and <strong>b</strong> is calculated as
            <el-image src="https://www.csuligroup.com/DrugGen/img/f1.png"></el-image>
            .
          </p>
          <p>
            <el-image src="https://www.csuligroup.com/DrugGen/img/f2.png" style="height: 25px"></el-image>
            is the sum of the lengths of each bond on the shortest path between two pharmacophore points, where the length of a single covalent single bond is set to 1, a double bond 0.87, an aromatic bond 0.91, a triple bond 0.78.
          </p>
          <p>
            <el-image src="https://www.csuligroup.com/DrugGen/img/f3.png" style="height: 25px"></el-image>
            is the distance between the center of the pharmacophore point and the edge of it. If the pharmacophore is a ring, then the distance is calculated as <strong>0.2*N</strong>, where N is number of heavy atoms in the pharmacophore point.</p>
          <p>
            For example, the shortest-path distances between A-B and A-C in the picture below can be calculated as:
          </p>
          <el-image src="https://www.csuligroup.com/DrugGen/img/f4.png"></el-image>
          <div style="text-align: center">
            <el-image src="https://www.csuligroup.com/DrugGen/img/sp_example.png" style=""></el-image>
          </div>

        </div>
        <br>
        <div class="content1">
          <h1>Generation</h1>
<!--          a.Submit task-->
          <div>
            <h2>a.Submit task</h2>
            <div style="text-align: center">
              <strong >Task Submit Panel</strong>
              <div class="part" style="">
                <el-image src="https://www.csuligroup.com/DrugGen/img/pgmg1.png" style=""></el-image>
              </div>
            </div>
            <div style="">
              <p>Upload or input the prepared the pharmacophore hypotheses. The specific format of
                the pharmacophore hypotheses can be seen in the <strong><a href="#first">Prepare the pharmacophore hypotheses</a></strong>. </p>
            </div>

            <div class="part" style="text-align: center">
              <el-image src="https://www.csuligroup.com/DrugGen/img/pgmg7.png" style=""></el-image>
            </div>
              <p>
                You will need the task ID to query the result, please save it carefully. If computational
                resources are sufficient, your task will be finished in about 10 minutes. After completion,
                the webpage will automatically redirect to the result page and display the results.
                See <strong> <a href="#second">Get result</a></strong> for displayed results.
              </p>


          </div>
<!--          b.Check Task Status and Results-->
          <div>
            <h2>b.Check Task Status and Results</h2>
            <div style="text-align: center">
              <strong>
                Result Panel
              </strong>
              <div  style="">
                <el-image class="part" src="https://www.csuligroup.com/DrugGen/img/pgmg2.png" style=""></el-image>
              </div>
            </div>

              <p>You can query the status of your submitted task through the task ID.
                If the task has completed, you can get the result using the task ID.</p>

          </div>
<!--          c.Get result-->
          <div id="second">
            <h2>c.Get result</h2>
            <h3>Result Display Page</h3>
            <div style="text-align: center">
              <strong>
                Click on the figure to get more details
              </strong>
              <div>
                <el-tooltip class="item" effect="dark" content="Your input information." placement="top-end">
                  <el-button>
                    <el-image class="part" src="https://www.csuligroup.com/DrugGen/img/pgmg4.png"  style=""></el-image>
                  </el-button>
                </el-tooltip>

                <el-tooltip class="item" effect="dark" content="Properties distribution.The properties(SA, QED，MolWt) of the generated compounds." placement="top-end">
                  <el-button>
                    <el-image class="part" src="https://www.csuligroup.com/DrugGen/img/pgmg5.png"  style=""></el-image>
                  </el-button>
                </el-tooltip>

                <el-tooltip class="item" effect="dark" content="2D molecular display, click on the detail bottom to get more details." placement="top-end">
                  <el-button>
                    <el-image class="part" src="https://www.csuligroup.com/DrugGen/img/pgmg6.png"  style=""></el-image>
                  </el-button>
                </el-tooltip>

<!--                <el-button @click="open1" >-->
<!--                  <el-image class="part" src="https://www.csuligroup.com/DrugGen/img/pgmg4.png"  style=""></el-image>-->
<!--                </el-button>-->
<!--                <el-button @click="open2" >-->
<!--                  <el-image class="part" src="https://www.csuligroup.com/DrugGen/img/pgmg5.png" style=""></el-image>-->
<!--                </el-button>-->
<!--                <el-button @click="open3" >-->
<!--                  <el-image class="part" src="https://www.csuligroup.com/DrugGen/img/pgmg6.png" style=""></el-image>-->
<!--                </el-button>-->

              </div>
            </div>

<!--            <el-badge :value="12" class="item" type="primary">-->
<!--              <el-image src="https://www.csuligroup.com/DrugGen/img/sp_example.png" style=""></el-image>-->
<!--            </el-badge>-->


          </div>
<!--          d.Download-->
          <div>
            <h2>d.Download</h2>

              <p>The user can download all of the generated molecules (SMILES string) via clicking the "download as csv"
                bottom.</p>


          </div>
        </div>
    </div>
  </div>
  </div>

</template>

<script>
export default {
  name: "Tutorial",
  data() {
    return {
    }
  },
  methods:{
    open1()
    {
      this.$notify({
        title: 'details',
        message: 'Your input information.',
        offset: 80
      });
    },
    open2()
    {

      this.$notify({
        title: 'Properties distribution',
        message: 'The properties(SA, QED，MolWt) of the generated compounds.',
        offset: 300
      });
    },
    open3()
    {
      this.$notify({
        title: 'details',
        message: '2D molecular display, click on the detail bottom to get more details.',
        position: 'bottom-right',
        offset: 100
      });
    }
  }
}
</script>

<style scoped>

  .part{
    margin-left:15px;
    margin-right:15px;
    border-radius: 10px;
    box-shadow: rgba(0, 0, 0, 0.35) 0px 3px 8px;
  }
.container {
  max-width: 1160px;
  margin: 0 auto;
}
  p,h1,h2{
    text-align: left;
    font-family: "Noto Sans SC";
  }
  p{
    line-height: 1.5em;
  }
.content1{
  box-shadow: rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px;
  background-color: RGB(255,255,255);
  border-radius: 10px;
  padding: 10px;
  /*height: 200px;*/
}
.content2{
  box-shadow: rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px;
  background-color: RGB(255,255,255);
  border-radius: 10px;
  padding: 10px;
  padding-bottom: 1px;
}
</style>
